
import type { FloaterStyle } from "../client/combat";

export const COMBAT_DECAL_LIFETIME_MS = 550;
export const COMBAT_DECAL_RADIUS_MM = 460;
export const COMBAT_DECAL_EPSILON_MM = 2;

export interface CombatDecalPoint {
  readonly xMm: number;
  readonly yMm: number;
  readonly zMm: number;
}

export interface CombatDecalScreenPoint {
  readonly x: number;
  readonly y: number;
}

type CombatDecalContext = Pick<
  CanvasRenderingContext2D,
  "save" | "restore" | "beginPath" | "moveTo" | "lineTo" | "closePath" | "fill" | "stroke"
> & {
  globalAlpha: number;
  fillStyle: CanvasRenderingContext2D["fillStyle"];
  strokeStyle: CanvasRenderingContext2D["strokeStyle"];
  lineWidth: number;
};

/** Keep a hit mark long enough to read, then fade its edge instead of popping. */
export function combatDecalAlpha(bornMs: number, nowMs: number): number {
  const age = nowMs - bornMs;
  if (age < 0 || age >= COMBAT_DECAL_LIFETIME_MS) return 0;
  const fadeStart = COMBAT_DECAL_LIFETIME_MS * 0.62;
  return age <= fadeStart ? 1 : 1 - (age - fadeStart) / (COMBAT_DECAL_LIFETIME_MS - fadeStart);
}

/** An eight-sided, ground-hugging footprint for a resolved hit. */
export function combatDecalPath(
  xMm: number,
  yMm: number,
  groundMm: number,
  radiusMm = COMBAT_DECAL_RADIUS_MM,
): readonly CombatDecalPoint[] {
  const points: CombatDecalPoint[] = [];
  for (let index = 0; index < 8; index += 1) {
    const angle = (index / 8) * Math.PI * 2 - Math.PI / 8;
    const radius = index % 2 === 0 ? radiusMm : radiusMm * 0.76;
    points.push({
      xMm: xMm + Math.cos(angle) * radius,
      yMm: yMm + Math.sin(angle) * radius,
      zMm: groundMm + COMBAT_DECAL_EPSILON_MM,
    });
  }
  return points;
}

function colourFor(style: FloaterStyle): string {
  if (style === "crit") return "#ffd45e";
  if (style === "block") return "#8fc9e8";
  return "#d8efff";
}

/** Draw the planar mark as a translucent filled shard with a readable edge. */
export function drawCombatDecal(
  ctx: CombatDecalContext,
  path: readonly CombatDecalPoint[],
  project: (point: CombatDecalPoint) => CombatDecalScreenPoint | null,
  style: FloaterStyle,
  alpha: number,
): void {
  if (alpha <= 0 || path.length < 3) return;
  const screen = path.map(project).filter((point): point is CombatDecalScreenPoint => point !== null);
  if (screen.length < 3) return;

  ctx.save();
  ctx.globalAlpha = alpha * 0.42;
  ctx.fillStyle = colourFor(style);
  ctx.beginPath();
  ctx.moveTo(screen[0].x, screen[0].y);
  for (const point of screen.slice(1)) ctx.lineTo(point.x, point.y);
  ctx.closePath();
  ctx.fill();

  ctx.globalAlpha = alpha * 0.9;
  ctx.strokeStyle = colourFor(style);
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(screen[0].x, screen[0].y);
  for (const point of screen.slice(1)) ctx.lineTo(point.x, point.y);
  ctx.closePath();
  ctx.stroke();
  ctx.restore();
}

/** The hit styles that have a real impact; misses intentionally leave no mark.
 *  Structural rather than a `Floater`: the mark is raised by the blow landing
 *  (`Impact`), not by the number that follows it. */
export function hasCombatDecal(mark: { readonly style: FloaterStyle }): boolean {
  return mark.style !== "miss";
}
